import { api } from '@/services/http';
import config from '@/config';
import { API_URLS } from '@/services/api-config';
import { User } from '@/types/user';
import {
  GeneratePresignedUploadRequest,
  GeneratePresignedUploadResponse,
  SampleAddressTemplateRequest,
  UploadValidationState,
  ValidateAddressStreamRequest,
} from '@/types/address';
import {
  base64ToBlob,
  isBase64TemplateResponse,
  normalizeStreamValidationResponse,
} from '@/utils/excelValidation';
import type {
  FlattenPdfRequest,
  PdfGeneratePresignedRequest,
  PdfPreviewRequest,
  PdfValidateRequest,
} from '@/types/pdf';
import type { GetProductsRequest, GetProductsResponse, GetServiceCardRequest, ServiceCardResponse } from '@/types/home';
import type { PriceRequest, PriceResponse } from '@/types/price';
import { HolidayRequest, HolidayResponse } from '@/types/delivery';
import type { OrderRequest, OrderResponse, ActivateOrderResponse, GetReceiptRequest, GetReceiptResponse } from '@/types/order';
import type { MyOrdersResponse } from '@/types/myOrders';
import type { CustomersResponse } from '@/types/customers';
import type { PaymentMethodsResponse } from '@/types/paymentMethod';

class CreateMailoutApi {
  getCurrentUserInfo = async (): Promise<User> => {
    return api.post<User>(`${config.VUE_APP_AWS_API}${API_URLS.currentUser}`);
  };

  downloadSampleAddressTemplate = async (payload: SampleAddressTemplateRequest): Promise<Blob> => {
    const response = await api.get<Blob>(API_URLS.sampleAddressTemplate, {
      params: payload,
      responseType: 'blob',
    });
    if (!response.type.includes('application/json')) {
      return response;
    }
    const jsonText = await response.text();
    const parsed = JSON.parse(jsonText) as unknown;
    if (!isBase64TemplateResponse(parsed)) {
      throw new Error('Invalid sample template response payload');
    }
    const contentType = parsed.contentType || 'application/octet-stream';
    return base64ToBlob(parsed.body, contentType);
  };

  generatePresignedUpload = async (
    payload: GeneratePresignedUploadRequest
  ): Promise<GeneratePresignedUploadResponse> => {
    return api.post<GeneratePresignedUploadResponse>(API_URLS.generatePresignedUpload, payload);
  };

  uploadFileToPresignedUrl = <T = unknown>(url: string, file: File): Promise<T> => {
    return api.put<T>(url, file, {
      skipAuth: true,
      headers: { 'Content-Type': file.type },
    });
  };

  validateAddressStream = async (
    payload: ValidateAddressStreamRequest
  ): Promise<UploadValidationState> => {
    const response = await fetch(API_URLS.validateAddressStream, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('excelUpload.errors.uploadFailed');
    }

    if (!response.body) {
      const fallback = await response.json().catch(() => ({}));
      return normalizeStreamValidationResponse(fallback);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let done = false;
    let buffer = '';
    const parsedChunks: unknown[] = [];

    while (!done) {
      const result = await reader.read();
      done = result.done;
      buffer += decoder.decode(result.value || new Uint8Array(), { stream: !done });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) continue;
        const normalized = line.startsWith('data:') ? line.slice(5).trim() : line;
        if (!normalized || normalized === '[DONE]') continue;
        try {
          parsedChunks.push(JSON.parse(normalized));
        } catch {
          // Ignore non-JSON chunks (e.g. progress text) and continue collecting.
        }
      }
    }

    if (buffer.trim()) {
      try {
        parsedChunks.push(JSON.parse(buffer.trim()));
      } catch {
        // Ignore trailing non-JSON content.
      }
    }

    const payloadToUse = parsedChunks.length > 0 ? parsedChunks[parsedChunks.length - 1] : {};

    return normalizeStreamValidationResponse(payloadToUse);
  };

  getPostCardDetails = async (params: GetServiceCardRequest): Promise<ServiceCardResponse> => {
    return api.get<ServiceCardResponse>(API_URLS.getPostCardDetails, { params });
  }

  postPdfGeneratePresigned = async (_payload: PdfGeneratePresignedRequest): Promise<any> => {
    return api.post<any>(API_URLS.pdfGeneratePresigned, _payload);
  };

  postPdfValidate = async (_payload: PdfValidateRequest): Promise<any> => {
    return api.post<any>(API_URLS.pdfValidate, _payload);
  };

  postFlattenPdf = async (_payload: FlattenPdfRequest): Promise<any> => {
    return api.post<any>(API_URLS.flattenPdf, _payload);
  };

  getPrice = async (_payload?: PriceRequest): Promise<PriceResponse> => {
    return api.post<PriceResponse>(API_URLS.calculatePrice, _payload);
  };

  getHolidays = async (_payload?: HolidayRequest): Promise<HolidayResponse> => {
    return api.post<HolidayResponse>(`${API_URLS.getHolidays}`, _payload);
  };

  getProducts = async (params: GetProductsRequest): Promise<GetProductsResponse> => {
    return api.get<GetProductsResponse>(API_URLS.getProducts, { params });
  };

  postOrder = async (payload: OrderRequest): Promise<OrderResponse> => {
    return api.post<OrderResponse>(API_URLS.createOrder, payload);
  };

  activateOrder = async (orderId: string, suOrderId: string): Promise<ActivateOrderResponse> => {
    return api.put<ActivateOrderResponse>(API_URLS.activateOrder(orderId, suOrderId));
  };

  getMyOrders = async (): Promise<MyOrdersResponse> => {
    return api.get<MyOrdersResponse>(API_URLS.getMyOrders);
  };

  postPdfPreview = async (payload: PdfPreviewRequest): Promise<any> => {
    return api.post<any>(API_URLS.pdfPreview, payload);
  };

  getCustomers = async (): Promise<CustomersResponse> => {
    return api.get<CustomersResponse>(API_URLS.getCustomers);
  };

  getPaymentMethods = async (): Promise<PaymentMethodsResponse> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        import('../../mocks/paymentMethods/paymentmethods.json').then((module) => {
          resolve(module.default as PaymentMethodsResponse);
        });
      }, config.VUE_API_WAIT_TIME);
    });
  };

  getReceipt = async (payload: GetReceiptRequest): Promise<GetReceiptResponse> => {
    return api.post<GetReceiptResponse>(API_URLS.getReceipt, payload);
  };
}

export default new CreateMailoutApi();
